import { Injectable } from "@angular/core";
import {
  GbGemeindeListEntry,
  GrundstueckId,
  GrundstueckListEntryMeta,
  RechtVersionListEntry,
  StandardrechtVersionDetailEntryMeta,
} from "../../core/gb-api";
import {
  LoadKantParzelleSearchRequest,
  MapResponse,
  MapsService,
} from "../../core/maps-api";
import { EMPTY, Observable, of } from "rxjs";
import { GemeindeListEntry } from "../../core/grundbuchmeldung-api";
import { TranslateService } from "@ngx-translate/core";
import { MapType } from "../enum/map-type";
import GrundstueckArtEnum = GrundstueckListEntryMeta.GrundstueckArtEnum;
import RechtArtEnum = RechtVersionListEntry.RechtArtEnum;

export interface GrundstueckKantparzelleMapLoaderType {
  grundstuecke: Array<string>;
  rowId: string;
}

export class NoLoaderError extends Error {}

@Injectable()
export class MapServiceFactory {
  constructor(
    private mapsService: MapsService,
    private translateService: TranslateService
  ) {}

  createFrom(type: MapType, model: any): Observable<MapResponse> {
    switch (type) {
      case MapType.grundstueck:
        return this.createGrundstueckMapLoader(model);
      case MapType.gemeinde:
        return this.createGemeindeMapLoader(model);
      case MapType.DIENSTBARKEIT:
      case MapType.GRUNDLAST:
        return this.createStandardRechtMapLoader(
          model,
          RechtArtEnum.DIENSTBARKEIT
        );
      case MapType.VORMERKUNG:
        return this.createStandardRechtMapLoader(
          model,
          RechtArtEnum.VORMERKUNG
        );
      case MapType.oereb:
        return this.createOerebMapLoader(model);
      case MapType.grundstueckKantparzelle:
        return this.createGrundstueckKantparzelleMapLoader(model);
      case MapType.eigentum:
        return this.createEigentumLoader(model);
    }
    throw new NoLoaderError(
      "No Loader found for type: " + type + " and model " + model
    );
  }

  private createStandardRechtMapLoader(
    recht: StandardrechtVersionDetailEntryMeta,
    art: RechtArtEnum
  ): Observable<MapResponse> {
    if (!recht || !recht.id || !recht.id.id) {
      return of(<MapResponse>{});
    }
    if (recht.histInfo.histEnum === "VON_RECHTGUELTIG_BIS_RECHTSGUELTIG") {
      return of(<MapResponse>{});
    }
    return this.mapsService.loadStandardrechtMap(
      art,
      recht.id.id,
      this.translateService.currentLang
    );
  }

  private createGrundstueckMapLoader(
    grundstueck: any
  ): Observable<MapResponse> {
    let id,
      bfsNr,
      kreisNr,
      nummer = "";
    if (
      grundstueck &&
      grundstueck.histInfo &&
      grundstueck.histInfo.histEnum &&
      grundstueck.histInfo.histEnum === "VON_RECHTGUELTIG_BIS_RECHTSGUELTIG"
    ) {
      return of(<MapResponse>{});
    }
    if (grundstueck && grundstueck.id) {
      id = grundstueck.id.id;
      const gsNr = grundstueck.grundstueckNummern[0];
      if (gsNr) {
        nummer = grundstueck.grundstueckNummern[0].nummer;
        if (gsNr.gbGemeinde) {
          bfsNr = grundstueck.grundstueckNummern[0].gbGemeinde.bfsNummer;
          kreisNr = grundstueck.grundstueckNummern[0].gbGemeinde.kreisNummer;
        }
      }
    } else {
      if (grundstueck && grundstueck.grundstueckId) {
        id = grundstueck.grundstueckId.id;
      }
      if (grundstueck && grundstueck.grundstueckNummer) {
        nummer = grundstueck.grundstueckNummer.nummer;
      }
      if (
        grundstueck &&
        grundstueck.grundstueckNummer &&
        grundstueck.grundstueckNummer.gbGemeinde
      ) {
        bfsNr = grundstueck.grundstueckNummer.gbGemeinde.bfsNummer;
        kreisNr = grundstueck.grundstueckNummer.gbGemeinde.kreisNummer;
      }
    }
    if (!id && !bfsNr && !kreisNr && !nummer) {
      return of(<MapResponse>{});
    }
    if (grundstueck.grundstueckArt === GrundstueckArtEnum.UNBEKANNT) {
      return of(<MapResponse>{});
    }
    return this.mapsService.loadGrundstueckMap(
      id,
      bfsNr,
      kreisNr,
      nummer,
      this.translateService.currentLang
    );
  }

  private createGrundstueckKantparzelleMapLoader(
    mapLoader: GrundstueckKantparzelleMapLoaderType
  ): Observable<MapResponse> {
    const req = <LoadKantParzelleSearchRequest>{};
    req.grundstueckIds = mapLoader.grundstuecke;
    req.rowId = mapLoader.rowId;
    return this.mapsService.loadGrundstueckKantparzelleMap(req);
  }

  private createGemeindeMapLoader(
    gemeinde: GemeindeListEntry | GbGemeindeListEntry
  ): Observable<MapResponse> {
    let bfsNr,
      kreisNr = "";
    if (!gemeinde) {
      return EMPTY;
    }
    if (gemeinde && gemeinde.hasOwnProperty("kreisNummer")) {
      kreisNr = gemeinde["kreisNummer"];
    }
    if (gemeinde) {
      bfsNr = gemeinde.bfsNummer;
    }
    if (!bfsNr && !kreisNr) {
      return EMPTY;
    }
    return this.mapsService.loadGemeindeMap(
      bfsNr,
      kreisNr,
      this.translateService.currentLang
    );
  }

  private createOerebMapLoader(
    grundstueckId: GrundstueckId
  ): Observable<MapResponse> {
    return this.mapsService.loadOerebMap(
      grundstueckId.id,
      null,
      null,
      null,
      this.translateService.currentLang
    );
  }

  private createEigentumLoader(
    grundstueckId: GrundstueckId
  ): Observable<MapResponse> {
    return this.mapsService.loadEigentumMap(
      grundstueckId.id,
      this.translateService.currentLang
    );
  }
}
